<template>
  <AppShell>
    <section class="view on soa-view-reports">
      <div class="pagehead"><div><h1>{{ t("reports_h") }}</h1><p class="sub">{{ t(tabSubs[tab] || "reports_sub") }}</p></div></div>

      <!-- The five insight views used to be their own sidebar group. They are four cuts of the same
           report plus the crawler log, so they live here as tabs and `?tab=` is the deep link. -->
      <div ref="tabRail" class="rtabs" role="tablist" :aria-label="t('reports_tabs')" @keydown="onTabKeys">
        <button
          v-for="(name, index) in tabs"
          :id="`rtab-${name}`"
          :key="name"
          ref="tabButtons"
          type="button"
          role="tab"
          class="rtab"
          :class="{ on: name === tab }"
          :aria-selected="name === tab ? 'true' : 'false'"
          :aria-controls="`rpanel-${name}`"
          :tabindex="name === tab ? 0 : -1"
          :data-index="index"
          @click="selectTab(name)"
        >{{ tabLabel(name) }}</button>
      </div>

      <div :id="`rpanel-${tab}`" role="tabpanel" class="rpanel" :aria-labelledby="`rtab-${tab}`" tabindex="0">
        <template v-if="tab === 'overview'">
          <p v-if="loading" class="empty" role="status">{{ t("loading") }}</p>
          <p v-else-if="error" class="empty" role="alert">{{ error }} <button type="button" class="atbtn" @click="load">{{ t("account_retry") }}</button></p>
          <div v-else-if="!currentBrand" class="empty">{{ t("ins_none") }}</div>
          <div v-else-if="!reports.length" class="empty">{{ t("no_reports") }}</div>
          <div v-else class="rlog">
            <article v-for="report in reports" :key="report.id || report.created" class="rrow" :style="{ '--fc': bandColor(report.band) }">
              <time class="rr-date" :datetime="isoTime(report.created)">{{ reportDate(report.created) }}</time>
              <div class="rr-main"><strong class="rr-brand">{{ report.brand }}</strong><span class="rr-band">{{ bandLabel(report.band) }}</span></div>
              <div class="rr-nums"><span><b>{{ Math.round(report.rec_pct || 0) }}%</b> {{ t("reports_soa") }}</span><span><b>{{ report.score || 0 }}</b><i>/100</i></span></div>
              <div class="rr-acts"><a v-if="report.report_url" class="rr-open" :href="report.report_url" target="_blank" rel="noopener">{{ t("open_report") }} <span aria-hidden="true">›</span><span class="sr-only"> {{ t("opens_new") }}</span></a><button type="button" class="rr-maya" :aria-label="t('reports_ask_maya', { b: report.brand })" @click="askMaya(report)"><img :src="mayaImage" alt=""></button></div>
            </article>
          </div>
        </template>
        <ImpactView v-else-if="tab === 'impact'" embedded />
        <FactCheckView v-else-if="tab === 'facts'" embedded />
        <CrawlersView v-else-if="tab === 'crawlers'" embedded />
        <InsightsView v-else :key="tab" :tab="tab" embedded />
      </div>
    </section>
  </AppShell>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { getFactPatrol } from "../api/geoExec";  // GEO_EXEC R10+R11
import { listBaselineRuns } from "../api/geoExecR5";  // GEO_EXEC_R5
import { listReports } from "../api/reports";
import AppShell from "../layout/AppShell.vue";
import { REPORT_TABS } from "../router";
import { currentBrand, loadBrands, selectBrand } from "../stores/brands";
import { featureHidden, featureOn, featureState, probeFeature } from "../stores/geoExecFeatures";
import { useI18n } from "../useI18n";
import CrawlersView from "./CrawlersView.vue";
import FactCheckView from "./FactCheckView.vue";  // GEO_EXEC R10+R11
import ImpactView from "./ImpactView.vue";  // GEO_EXEC_R5
import InsightsView from "./InsightsView.vue";

const router = useRouter();
const route = useRoute();
const { lang, t, bandLabel } = useI18n();
const mayaImage = "/assets/img/maya-256.webp";
const allReports = ref([]), loading = ref(true), error = ref("");
const tabButtons = ref([]), tabRail = ref(null);
let controller;
const tabLabels = { overview: "reports_tab_log", impact: "nav_impact", facts: "reports_tab_facts", competitors: "nav_competitors", citations: "nav_citations", prompts: "nav_prompts", sentiment: "nav_sentiment", crawlers: "nav_crawlers" };
// The sub-line each insight used to carry as its own page — kept, because it is what explains the
// section; only the duplicate <h1> went away when they became tabs.
const tabSubs = { overview: "reports_sub", impact: "impact_sub", facts: "facts_sub", competitors: "comp_sub", citations: "cit_sub", prompts: "pr_sub", sentiment: "sen_sub", crawlers: "cw_sub" };

// FE-5 · Impact (R5) and the facts tab (R10+R11) are switched features, so the rule the nav used
// applies to the tab: it is drawn once its switch has been CONFIRMED on, by a real read done on
// mount. Both go the same way. The facts tab was first written the other way — visible until a
// 404 says otherwise — which meant a client on a deployment without R11 clicked the tab and
// watched the whole rail lose an entry under their hand. Probing is the honest order.
//
// A deep link is answered by the verdicts rather than by the rail: `/impact` or `/fact-check`
// from a bookmark still opens its tab while the switch is merely unknown, and falls back to the
// report log only once the switch has actually answered "off".
const tabs = computed(() => REPORT_TABS.filter((name) => {
  if (name === "impact") return featureOn("r5") || tab.value === "impact";
  if (name === "facts") return featureOn("factCheck") || tab.value === "facts";
  return true;
}));
const tab = computed(() => {
  const wanted = String(route.query.tab || "");
  if (!REPORT_TABS.includes(wanted)) return "overview";
  if (wanted === "impact" && featureState("r5") === "off") return "overview";
  if (wanted === "facts" && featureHidden("factCheck")) return "overview";
  return wanted;
});
const reports = computed(() => allReports.value.filter((report) => currentBrand.value && String(report.brand || "").toLowerCase() === String(currentBrand.value.brand || "").toLowerCase()));
function tabLabel(name) { return t(tabLabels[name]); }
// `brand_id` (and anything else the caller arrived with) survives a tab switch; only `tab` moves.
function selectTab(name) { router.push({ path: "/reports", query: { ...route.query, tab: name === "overview" ? undefined : name } }); }
function onTabKeys(event) {
  const step = { ArrowRight: 1, ArrowLeft: -1, Home: -REPORT_TABS.length, End: REPORT_TABS.length }[event.key];
  if (step === undefined) return;
  event.preventDefault();
  const open = tabs.value;
  const next = Math.min(open.length - 1, Math.max(0, open.indexOf(tab.value) + step));
  selectTab(open[next]);
}
function bandColor(band) { return ["leader", "darkhorse"].includes(String(band).toLowerCase()) ? "var(--jade)" : ["challenger", "rising"].includes(String(band).toLowerCase()) ? "var(--amber)" : "var(--verm)"; }
function isoTime(timestamp) { return timestamp ? new Date(timestamp * 1000).toISOString() : undefined; }
function reportDate(timestamp) { return timestamp ? new Date(timestamp * 1000).toLocaleDateString(lang.value === "zh" ? "zh-CN" : lang.value === "ja" ? "ja-JP" : "en-US", { year: "numeric", month: "short", day: "numeric" }) : ""; }
async function load() { controller?.abort(); controller = new AbortController(); loading.value = true; error.value = ""; try { await loadBrands({ signal: controller.signal }); if (route.query.brand_id) selectBrand(route.query.brand_id); const data = await listReports({ signal: controller.signal }); allReports.value = data.runs || []; } catch (caught) { if (caught.name !== "AbortError") error.value = caught.message || t("reports_load_error"); } finally { loading.value = false; } }
function askMaya(report) { try { sessionStorage.setItem("soa_report_handoff", JSON.stringify({ brand: report.brand || "", report: report.id || "" })); } catch { /* Maya remains usable without storage. */ } router.push("/maya"); }
function activeTabButton() {
  return tabButtons.value.find((element) => Number(element?.dataset?.index) === tabs.value.indexOf(tab.value));
}
// On a phone the rail scrolls sideways, so a deep link to a later tab would open with its own tab
// off screen. Scroll the rail itself rather than calling scrollIntoView, which also moves the page.
function revealTab() {
  const rail = tabRail.value, button = activeTabButton();
  if (!rail || !button || rail.scrollWidth <= rail.clientWidth) return;
  rail.scrollLeft = Math.max(0, button.offsetLeft - (rail.clientWidth - button.clientWidth) / 2);
}
// Keyboard tab changes must take the focus with them, or the next arrow press is lost.
watch(tab, () => requestAnimationFrame(() => {
  const button = activeTabButton();
  if (button && tabButtons.value.some((element) => element === document.activeElement)) button.focus();
  revealTab();
}));
onMounted(() => requestAnimationFrame(revealTab));
onMounted(load);
// One real read each decides whether the Impact (R5) and facts (R10+R11) tabs exist. Both
// verdicts are cached for the browser tab, so this costs one request apiece per session and
// nothing on a revisit — and `probeFeature` shares the verdict with the views themselves, which
// report the same switch through `noteFeature` when they load.
watch(() => currentBrand.value?.id, (brandId) => {
  if (!brandId) return;
  probeFeature("r5", () => listBaselineRuns(brandId));
  probeFeature("factCheck", () => getFactPatrol(brandId));
}, { immediate: true });
onBeforeUnmount(() => controller?.abort());
</script>

<style scoped>
.soa-view-reports { max-width: 1040px; }
/* A rule the row sits on, so the selected tab's underline reads as part of one line rather than a
   floating mark. Scrolls sideways on a phone instead of wrapping into two ragged rows. */
.rtabs {
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--line);
  overflow-x: auto;
  scrollbar-width: none;
}
.rtabs::-webkit-scrollbar { display: none; }
.rtab {
  flex: 0 0 auto;
  position: relative;
  padding: 9px 14px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: var(--fs-subhead);
  font-weight: 600;
  color: var(--mute);
  cursor: pointer;
  white-space: nowrap;
  transition: color var(--dur-press) var(--ease-out);
}
.rtab::after {
  content: "";
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: -1px;
  height: 2px;
  border-radius: 2px;
  background: var(--accent-fill);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform var(--dur) var(--ease-out);
}
.rtab:hover { color: var(--ink); }
.rtab.on { color: var(--ink); }
.rtab.on::after { transform: scaleX(1); }
.rtab:focus-visible { outline: 3px solid var(--indigo); outline-offset: -2px; border-radius: 8px; }
.rpanel:focus-visible { outline: none; }
@media (prefers-reduced-motion: reduce) {
  .rtab, .rtab::after { transition: none; }
}
</style>
